/**
 * MissionRunner.js — Phase 39.18 (Feb 2026)
 * Generic 14-mission animated learning engine for CognitiaIndia.com's
 * non-technical courses (Civic Sense, Life Skills, Ethics, Financial Literacy…).
 *
 * Mission JSON schema (each mission is one of):
 *   { type: 'intro'|'concepts'|'right_vs_wrong'|'scenario'|'decision'
 *          |'rights_boundaries'|'communication'|'emotional'|'community'
 *          |'safety'|'digital'|'simulation'|'be_the_change'|'action_plan',
 *     title, character, wisdom, points?, choices?, right?, wrong? … }
 *
 * The engine hard-codes the 14-slot progression (order matters) and asks the
 * data file for the content per slot.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, ArrowRight, RotateCw, Sparkles, Star, Trophy, Check, X, Lightbulb, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { COURSES } from '../data/nonTechCourses';

const API = process.env.REACT_APP_BACKEND_URL;

/* ------------------------------ SVG Caricatures ------------------------------ */
// 14 distinct human caricatures — friendly, diverse, expressive. Palette-driven
// so we can reuse the same silhouette with different colours to imply role.
const AVATARS = {
  student:      { hair: '#2c1810', skin: '#f4c8a8', shirt: '#3b82f6', label: 'Student' },
  student_f:    { hair: '#1a1a1a', skin: '#e8b48a', shirt: '#ec4899', label: 'Student' },
  parent:       { hair: '#4a2f1f', skin: '#f4c8a8', shirt: '#059669', label: 'Parent' },
  teacher:      { hair: '#666',    skin: '#e8b48a', shirt: '#7c3aed', label: 'Teacher' },
  police:       { hair: '#1a1a1a', skin: '#c99b7b', shirt: '#1e3a8a', label: 'Police Officer' },
  elder:        { hair: '#e5e5e5', skin: '#e8b48a', shirt: '#78716c', label: 'Elder' },
  child:        { hair: '#3b2016', skin: '#f4c8a8', shirt: '#f59e0b', label: 'Child' },
  professional: { hair: '#2a1a10', skin: '#c99b7b', shirt: '#0f172a', label: 'Professional' },
  official:     { hair: '#3a2617', skin: '#c99b7b', shirt: '#dc2626', label: 'Official' },
  shopkeeper:   { hair: '#2c1810', skin: '#e8b48a', shirt: '#a16207', label: 'Shopkeeper' },
  colleague:    { hair: '#1a1a1a', skin: '#f4c8a8', shirt: '#0891b2', label: 'Colleague' },
  citizen:      { hair: '#4a2f1f', skin: '#e8b48a', shirt: '#10b981', label: 'Citizen' },
  neighbour:    { hair: '#3b2016', skin: '#f4c8a8', shirt: '#8b5cf6', label: 'Neighbour' },
  hero:         { hair: '#1a1a1a', skin: '#c99b7b', shirt: '#f43f5e', label: 'Community Hero' },
};

// A single reusable caricature — expressive face + mouth + body. Face expression varies.
const Caricature = ({ id = 'student', expression = 'smile', size = 140, testid }) => {
  const c = AVATARS[id] || AVATARS.student;
  const mouth = expression === 'smile' ? 'M35 63 Q50 73 65 63' :
                expression === 'sad'   ? 'M35 70 Q50 60 65 70' :
                expression === 'shock' ? 'M42 68 Q50 76 58 68 Q50 62 42 68' :
                expression === 'talk'  ? 'M40 65 Q50 72 60 65 Q50 68 40 65' :
                                          'M40 65 L60 65'; // neutral
  return (
    <svg viewBox="0 0 100 130" width={size} height={size * 1.3} data-testid={testid || `caricature-${id}`}>
      {/* body */}
      <path d={`M20 130 L20 95 Q20 80 35 78 L65 78 Q80 80 80 95 L80 130 Z`} fill={c.shirt} />
      {/* neck */}
      <rect x="42" y="70" width="16" height="12" fill={c.skin} />
      {/* head */}
      <ellipse cx="50" cy="45" rx="24" ry="28" fill={c.skin} />
      {/* hair */}
      <path d="M26 40 Q26 18 50 18 Q74 18 74 40 Q74 34 65 32 L35 32 Q26 34 26 40 Z" fill={c.hair} />
      {/* eyes */}
      <circle cx="41" cy="46" r="2.5" fill="#111" />
      <circle cx="59" cy="46" r="2.5" fill="#111" />
      <circle cx="41.5" cy="45.3" r="0.8" fill="#fff" />
      <circle cx="59.5" cy="45.3" r="0.8" fill="#fff" />
      {/* mouth */}
      <path d={mouth} stroke="#111" strokeWidth="2" fill="none" strokeLinecap="round" />
      {/* cheeks */}
      <circle cx="34" cy="56" r="3" fill="#f8a5a5" opacity="0.6" />
      <circle cx="66" cy="56" r="3" fill="#f8a5a5" opacity="0.6" />
    </svg>
  );
};

/* ------------------------------ Bubbles ------------------------------ */
const SpeechBubble = ({ children, side = 'right', testid }) => (
  <div
    data-testid={testid || 'speech-bubble'}
    className={`relative bg-white text-slate-800 rounded-2xl px-5 py-3 shadow-lg max-w-md ${side === 'right' ? 'ml-6' : 'mr-6'}`}
    style={{ fontSize: '0.95rem' }}
  >
    <div
      className={`absolute w-4 h-4 bg-white transform rotate-45 ${side === 'right' ? '-left-2 top-6' : '-right-2 top-6'}`}
    />
    {children}
  </div>
);

const WisdomBubble = ({ text, testid }) => (
  <div
    data-testid={testid || 'wisdom-bubble'}
    className="mt-6 mx-auto max-w-2xl bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-400 rounded-2xl p-4 shadow-md"
  >
    <div className="flex items-center gap-2 mb-2 text-amber-800">
      <Sparkles className="w-4 h-4" />
      <span className="font-bold text-sm uppercase tracking-wider">Words of Wisdom</span>
    </div>
    <p className="text-slate-800 italic text-base leading-relaxed">&ldquo;{text}&rdquo;</p>
  </div>
);

/* ------------------------------ Mission Renderers ------------------------------ */
const MissionScene = ({ m, onAdvance, awardXP }) => {
  const [choice, setChoice] = useState(null);
  const [showReveal, setShowReveal] = useState(false);

  const handleChoice = (idx) => {
    if (choice !== null) return;
    setChoice(idx);
    setShowReveal(true);
    const correct = m.choices?.[idx]?.correct;
    if (correct) {
      awardXP(m.points || 10, m.badge);
      toast.success(`+${m.points || 10} XP · ${m.choices[idx].reason || 'Nice choice!'}`);
    } else {
      toast.info(m.choices[idx].reason || 'Think again — see the consequence.');
    }
  };

  const showActionButton = m.type !== 'decision' || showReveal;

  return (
    <div className="p-4 md:p-8">
      {/* Character + Speech */}
      <div className="flex items-start justify-center gap-6 flex-wrap">
        <div className="flex flex-col items-center">
          {m.avatar_url ? (
            <img
              src={m.avatar_url}
              alt={AVATARS[m.character]?.label || 'Instructor'}
              data-testid={`mission-avatar-${m.character}`}
              className="w-40 h-40 rounded-2xl object-cover ring-4 ring-primary/30 shadow-xl"
            />
          ) : (
            <Caricature id={m.character} expression={m.expression || 'smile'} size={160} />
          )}
          <span className="text-xs mt-1 text-muted-foreground">{AVATARS[m.character]?.label}</span>
        </div>
        {m.dialogue && <SpeechBubble>{m.dialogue}</SpeechBubble>}
      </div>

      {/* Learning points */}
      {m.points_list && (
        <div className="mt-6 max-w-2xl mx-auto space-y-2">
          {m.points_list.map((p, i) => (
            <div key={i} className="flex items-start gap-2 bg-white/60 dark:bg-slate-800/60 rounded-lg p-3 border border-slate-200 dark:border-slate-700">
              <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5" />
              <p className="text-sm">{p}</p>
            </div>
          ))}
        </div>
      )}

      {/* Right vs Wrong split */}
      {m.right && m.wrong && (
        <div className="grid grid-cols-2 gap-4 mt-6 max-w-3xl mx-auto">
          <div className="border-2 border-red-400 rounded-xl p-4 bg-red-50 dark:bg-red-950/20" data-testid="rvw-wrong">
            <div className="flex items-center gap-2 text-red-700 font-bold mb-2"><X className="w-4 h-4" /> Wrong</div>
            <p className="text-sm">{m.wrong}</p>
          </div>
          <div className="border-2 border-green-500 rounded-xl p-4 bg-green-50 dark:bg-green-950/20" data-testid="rvw-right">
            <div className="flex items-center gap-2 text-green-700 font-bold mb-2"><Check className="w-4 h-4" /> Right</div>
            <p className="text-sm">{m.right}</p>
          </div>
        </div>
      )}

      {/* Decision-based choices */}
      {m.choices && (
        <div className="mt-6 max-w-2xl mx-auto space-y-2" data-testid="mission-choices">
          {m.choices.map((c, i) => (
            <button
              key={i}
              onClick={() => handleChoice(i)}
              data-testid={`choice-${i}`}
              disabled={choice !== null}
              className={`w-full text-left p-3 rounded-lg border-2 transition ${
                choice === i
                  ? (c.correct ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : 'border-red-400 bg-red-50 dark:bg-red-950/20')
                  : choice !== null ? 'opacity-40 border-slate-300' : 'border-slate-300 hover:border-sky-500 hover:bg-sky-50 dark:hover:bg-sky-950/20'
              }`}
            >
              <div className="flex items-center justify-between">
                <span>{c.text}</span>
                {choice === i && (c.correct ? <Check className="w-4 h-4 text-green-600" /> : <X className="w-4 h-4 text-red-600" />)}
              </div>
              {choice === i && c.reason && <p className="text-xs mt-2 text-muted-foreground">{c.reason}</p>}
            </button>
          ))}
        </div>
      )}

      {m.wisdom && <WisdomBubble text={m.wisdom} />}

      {showActionButton && (
        <div className="mt-6 flex justify-center">
          <Button size="lg" onClick={onAdvance} data-testid="continue-btn" className="bg-emerald-600 hover:bg-emerald-500">
            Continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
};

/* ------------------------------ Final Quiz ------------------------------ */
const FinalQuiz = ({ quiz, xp, badges, onFinish, courseId }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const answer = (qi, ci) => !submitted && setAnswers((a) => ({ ...a, [qi]: ci }));
  const score = useMemo(() => quiz.filter((q, i) => answers[i] === q.correct).length, [answers, quiz]);
  const pct = Math.round((score / quiz.length) * 100);
  const passed = pct >= 70;

  const downloadCertificate = async () => {
    if (!courseId) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/courses/${courseId}/certificate?percentage=${pct}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CognitiaIndia_Certificate.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Certificate downloaded!');
    } catch (e) {
      toast.error(e.message || 'Failed to download certificate');
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold mb-1 flex items-center gap-2"><Trophy className="w-6 h-6 text-amber-500" /> Final Quiz</h2>
      <p className="text-sm text-muted-foreground mb-6">10 scenario-based questions · pass with 70%</p>
      <div className="space-y-4">
        {quiz.map((q, qi) => (
          <Card key={qi} className="p-4" data-testid={`quiz-q-${qi}`}>
            <p className="font-medium mb-3">{qi + 1}. {q.q}</p>
            <div className="space-y-1.5">
              {q.opts.map((o, ci) => {
                const chosen = answers[qi] === ci;
                const isCorrect = submitted && ci === q.correct;
                const isWrong = submitted && chosen && ci !== q.correct;
                return (
                  <button
                    key={ci}
                    onClick={() => answer(qi, ci)}
                    data-testid={`quiz-q${qi}-opt${ci}`}
                    disabled={submitted}
                    className={`w-full text-left p-2.5 rounded border text-sm ${
                      isCorrect ? 'border-green-500 bg-green-50 dark:bg-green-950/30' :
                      isWrong ? 'border-red-400 bg-red-50 dark:bg-red-950/20' :
                      chosen ? 'border-sky-500 bg-sky-50 dark:bg-sky-950/30' :
                      'border-slate-300 hover:border-sky-400'
                    }`}
                  >
                    {o}
                  </button>
                );
              })}
            </div>
            {submitted && q.explain && (
              <p className="mt-2 text-xs text-muted-foreground italic">💡 {q.explain}</p>
            )}
          </Card>
        ))}
      </div>
      {!submitted ? (
        <Button
          className="mt-6 w-full bg-emerald-600 hover:bg-emerald-500 text-lg py-6"
          onClick={() => setSubmitted(true)}
          disabled={Object.keys(answers).length < quiz.length}
          data-testid="quiz-submit"
        >
          Submit ({Object.keys(answers).length}/{quiz.length})
        </Button>
      ) : (
        <div className={`mt-6 rounded-xl p-6 text-center ${passed ? 'bg-emerald-100 dark:bg-emerald-950/30' : 'bg-amber-100 dark:bg-amber-950/30'}`}>
          <div className="text-4xl font-black mb-2" data-testid="quiz-score">{pct}%</div>
          <p className="text-sm mb-4">{score}/{quiz.length} correct · XP earned {xp} · Badges {badges.length}</p>
          {passed ? (
            <>
              <div className="flex items-center justify-center gap-2 text-emerald-700 font-bold text-lg">
                <Trophy className="w-5 h-5" /> Certificate Eligible!
              </div>
              <Button
                className="mt-4 mr-2 bg-amber-500 hover:bg-amber-400 text-white"
                onClick={downloadCertificate}
                disabled={downloading}
                data-testid="download-certificate-btn"
              >
                {downloading ? 'Preparing…' : '🏆 Download Certificate (PDF)'}
              </Button>
            </>
          ) : (
            <p className="text-amber-800">Passing score is 70% — replay some missions and try again.</p>
          )}
          <Button className="mt-4" onClick={onFinish} data-testid="quiz-finish">Finish</Button>
        </div>
      )}
    </div>
  );
};

/* ------------------------------ Main Runner ------------------------------ */
export default function MissionRunner() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(() => COURSES[courseId] || null);
  const [loading, setLoading] = useState(!COURSES[courseId]);
  const [loadError, setLoadError] = useState(null);
  const [missionIdx, setMissionIdx] = useState(0);
  const [xp, setXp] = useState(0);
  const [badges, setBadges] = useState([]);
  const [phase, setPhase] = useState('mission'); // 'mission' | 'quiz' | 'done'

  // Phase 40.19 — For DB-backed non-programming courses (course_id like
  // course_002b3f43374d), fetch the mission deck from the backend.
  useEffect(() => {
    if (COURSES[courseId]) return; // already have a hand-crafted deck
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/courses/${courseId}/missions`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`Failed to load mission deck (${res.status})`);
        const data = await res.json();
        if (!cancelled) {
          setCourse(data);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err.message || 'Failed to load');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [courseId]);

  if (loading) {
    return (
      <div className="p-8 text-center" data-testid="mission-loading">
        <p className="text-lg mb-4 text-muted-foreground">Loading missions…</p>
      </div>
    );
  }

  if (!course || loadError) {
    return (
      <div className="p-8 text-center">
        <p className="text-lg mb-4">{loadError || 'Course not found.'}</p>
        <Button onClick={() => navigate('/courses')}>Back to Courses</Button>
      </div>
    );
  }

  const total = course.missions.length;
  const m = course.missions[missionIdx];

  const awardXP = (points, badge) => {
    setXp((x) => x + points);
    if (badge && !badges.includes(badge)) setBadges((b) => [...b, badge]);
  };

  const advance = () => {
    if (missionIdx < total - 1) setMissionIdx((i) => i + 1);
    else setPhase('quiz');
  };
  const back = () => { if (missionIdx > 0) setMissionIdx((i) => i - 1); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-amber-50 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900">
      {/* Header */}
      <header className="bg-white/80 dark:bg-slate-900/80 backdrop-blur border-b border-slate-200 dark:border-slate-800 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/courses')} data-testid="mission-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground truncate">{course.name}</p>
            <h1 className="text-base font-bold truncate">
              {phase === 'quiz' ? 'Final Quiz' : `Mission ${missionIdx + 1} of ${total} · ${m?.title || ''}`}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/story/${courseId}/${missionIdx}`)}
              data-testid="mission-story-btn"
              title="Watch a 3-min animated story about this chapter"
            >
              🎬 Story
            </Button>
            <div className="flex items-center gap-1 text-amber-600" title="XP" data-testid="xp-badge">
              <Star className="w-4 h-4 fill-amber-400" /> <span className="font-bold">{xp}</span>
            </div>
            <div className="flex items-center gap-1 text-rose-500" title="Badges" data-testid="badge-count">
              <Heart className="w-4 h-4 fill-rose-400" /> <span className="font-bold">{badges.length}</span>
            </div>
          </div>
        </div>
        <Progress value={phase === 'quiz' ? 100 : ((missionIdx + 1) / total) * 90} className="h-1 rounded-none" />
      </header>

      {phase === 'mission' && m && (
        <MissionScene key={missionIdx} m={m} onAdvance={advance} awardXP={awardXP} />
      )}
      {phase === 'quiz' && (
        <FinalQuiz quiz={course.quiz} xp={xp} badges={badges} onFinish={() => navigate('/courses')} courseId={courseId} />
      )}

      {phase === 'mission' && (
        <footer className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={back} disabled={missionIdx === 0} data-testid="mission-prev">
            <ArrowLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <span className="text-xs text-muted-foreground">Mission {missionIdx + 1} / {total}</span>
          <Button variant="outline" size="sm" onClick={() => setMissionIdx(0)} data-testid="mission-restart">
            <RotateCw className="w-4 h-4 mr-1" /> Restart
          </Button>
        </footer>
      )}
    </div>
  );
}
